import React, { useState } from 'react';
import { API } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';

import { LedgerEntry } from '../../context/model/types';
import { createLedgerEntry, updateLedgerEntry, deleteLedgerEntry } from '../../graphql/mutations';
import { useUsers } from '../../context/users';
import { becamePaid as didBecomePaid, dollarsToCents, enqueuePmsJob, paymentJobId } from '../../integrations/pms';

interface Props {
  entry: LedgerEntry;
  setShow: (v: boolean) => void;
}

const TYPE_SUGGESTIONS = [
  'Rent',
  'Move in fee',
  'Move out fee',
  'Pickup',
  'Delivery',
  'Rent + move',
];

// month is stored as 'YYYY-MM'. <input type="month"> uses the same shape.
const todayMonth = (): string => new Date().toISOString().slice(0, 7);

const LedgerEntryModal: React.FC<Props> = ({ entry, setShow }) => {
  const { setLedgerEntries, facilityInfo } = useUsers();
  const isNew = !entry.id;

  const [name, setName] = useState(entry.name ?? '');
  const [date, setDate] = useState(entry.date ?? new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState<string>(entry.amount != null ? String(entry.amount) : '');
  const [type, setType] = useState(entry.type ?? 'Rent');
  const [month, setMonth] = useState(entry.month ?? todayMonth());
  const [notes, setNotes] = useState(entry.notes ?? '');
  const [paid, setPaid] = useState<boolean>(entry.paid ?? true);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const buildPayload = (): Partial<LedgerEntry> => ({
    name: name.trim() || null,
    date: date || null,
    amount: amount.trim() === '' ? null : Number(amount),
    type: type.trim() || null,
    month: month || null,
    notes: notes.trim() || null,
    paid,
  });

  const handleSave = async () => {
    if (!name.trim()) { alert('Name is required.'); return; }
    if (amount.trim() !== '' && Number.isNaN(Number(amount))) { alert('Amount must be a number.'); return; }
    setSaving(true);

    // Computed from props BEFORE the write. See becamePaid's own notes: a new
    // entry saved as paid is a payment being recorded (the Zelle case), while
    // an existing entry only counts if it was explicitly unpaid.
    const becamePaid = didBecomePaid({ isNew, previous: entry.paid, next: paid });
    const entryId = isNew ? uuidv4() : entry.id;
    const facilityID = entry.facilityID || facilityInfo?.id || null;

    try {
      if (isNew) {
        const input = {
          id: entryId,
          facilityID,
          ...buildPayload(),
        };
        await API.graphql({ query: createLedgerEntry, variables: { input } });
        setLedgerEntries(prev => [...prev, input as LedgerEntry]);
      } else {
        const input = { id: entry.id, ...buildPayload() };
        await API.graphql({ query: updateLedgerEntry, variables: { input } });
        setLedgerEntries(prev => prev.map(e => e.id === entry.id ? { ...e, ...input } as LedgerEntry : e));
      }

      // Record the payment in the facility's storage software. Keyed on the
      // ledger entry with no date, so re-saving this row can never post twice.
      const amountValue = Number(amount);
      if (becamePaid && Number.isFinite(amountValue) && amountValue > 0) {
        try {
          await enqueuePmsJob({
            facilityID,
            op: 'recordPayment',
            binsID: entryId,
            jobID: paymentJobId(entryId),
            payload: {
              binsTenantID: entry.tenantID || '',
              binsLedgerEntryID: entryId,
              amountCents: dollarsToCents(amountValue),
              paidAtISO: date || new Date().toISOString().slice(0, 10),
              reference: notes.trim() || 'manual',
              tenantName: name.trim() || null,
              // A combined charge has no rent-only amount of its own; the PMS
              // must not be sent our valet fees. Flagged for review in shadow mode.
              mixedCharge: /\+/.test(type),
            },
          });
        } catch (pmsError) {
          console.warn('[pms] recordPayment enqueue failed:', pmsError);
        }
      }

      setShow(false);
    } catch (e) {
      console.error('Error saving ledger entry:', e);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this ledger entry? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await API.graphql({ query: deleteLedgerEntry, variables: { input: { id: entry.id } } });
      setLedgerEntries(prev => prev.filter(e => e.id !== entry.id));
      setShow(false);
    } catch (e) {
      console.error('Error deleting ledger entry:', e);
      alert('Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
      onClick={e => { if (e.target === e.currentTarget) setShow(false); }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 10,
          padding: '24px 28px',
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 600, color: '#111827' }}>
          {isNew ? 'Add Ledger Entry' : 'Ledger Entry'}
        </h2>

        <Row label="Name">
          <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} />
        </Row>
        <Row label="Date">
          <input style={inputStyle} type="date" value={date} onChange={e => setDate(e.target.value)} />
        </Row>
        <Row label="Amount ($)">
          <input style={inputStyle} type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
        </Row>
        <Row label="Type">
          <input
            style={inputStyle}
            value={type}
            onChange={e => setType(e.target.value)}
            list="ledger-type-options"
          />
          <datalist id="ledger-type-options">
            {TYPE_SUGGESTIONS.map(t => <option key={t} value={t} />)}
          </datalist>
        </Row>
        <Row label="Billing month">
          <input style={inputStyle} type="month" value={month} onChange={e => setMonth(e.target.value)} />
        </Row>
        <Row label="Notes">
          <input style={inputStyle} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Prorated, Discounted" />
        </Row>
        <Row label="Paid">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
            <input
              type="checkbox"
              checked={paid}
              onChange={e => setPaid(e.target.checked)}
              style={{ accentColor: '#7B1FA2', cursor: 'pointer' }}
            />
            {paid ? 'Paid' : 'Unpaid'}
          </label>
        </Row>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
          {isNew ? <span /> : (
            <button
              onClick={handleDelete}
              disabled={deleting || saving}
              style={{
                padding: '8px 12px', borderRadius: 6, border: 'none',
                background: 'transparent', color: '#B91C1C', fontSize: 12, cursor: 'pointer',
              }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShow(false)}
              style={{
                padding: '8px 16px', borderRadius: 6,
                border: '1px solid #D1D5DB', background: '#fff',
                fontSize: 13, cursor: 'pointer', color: '#374151',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || deleting}
              style={{
                padding: '8px 16px', borderRadius: 6, border: 'none',
                background: saving ? '#C084FC' : '#7B1FA2',
                color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving…' : isNew ? 'Add Entry' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
    <label style={{ fontSize: 12, color: '#6B7280', minWidth: 110 }}>{label}</label>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 9px',
  borderRadius: 6,
  border: '1px solid #D1D5DB',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
};

export default LedgerEntryModal;
